import PostForm from "@serie3/mymediaplanner/Modules/Social/Forms/PostForm";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import NewModal from "../../shared/Modals/NewModal";
import SaveButton from "../../shared/Buttons/SaveButton";
import Post from "@serie3/mymediaplanner/Modules/Social/Entity/Post";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import TabTitle from "../../shared/Tabs/TabTitle";
import ComposePostTab from "./ComposPostTab";
import SchedulePostTab from "./SchedulePostTab";
import StatsTab from "./StatsTab";
import { useEffect, useRef, useMemo, useCallback, useState } from "react";
import TelepromterWrapper from "./TeleprompterWrapper";
import GroupCommentsTab from "./Groups/GroupCommentsTab";
import PostPlatformSettings from "./Settings/PostPlatformSettings";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import listHandler from "@serie3/common/Domain/listHandler";

const PostFormView = ({
  showEdit,
  setShowEdit,
  editData,
  dataList,
  setDataList,
  setEditData,
  trigger,
  setTrigger,
  customListHander = false,
  refreshOnClose = false,
  share = null,
  group = null,
  pillarsEndpoint = null,
}) => {
  const [isSaving, setIsSaving] = useState(false);

  // The form config is memoized once (see below), so it must never close over
  // the dataList array itself — that snapshot goes stale the moment the board
  // list changes (e.g. a second post is created) and saving would then write
  // the post back into an outdated list.
  const dataListRef = useRef(dataList);
  dataListRef.current = dataList;

  // Memoize form config — recreating it on every render was causing the onSubmit
  // closure to be stale and the form object to be thrown away unnecessarily.
  const editPostForm = useMemo(
    () => PostForm.createPostForm(() => dataListRef.current, setDataList, setShowEdit, false, customListHander, setIsSaving),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { t } = useTranslation();
  const formik = useFormik(editPostForm);

  // A change made in the schedule tab (set ready, plan created or deleted,
  // publish now) changes the post's status. The board behind the modal only
  // knows what it was given, so hand it the post as the API now sees it; the
  // same two channels the save button uses. Unsaved compose edits stay in
  // Formik, which is not touched here.
  const onPostChanged = useCallback((post) => {
    if (!post?.id) return;
    if (customListHander) {
      customListHander({ ok: true, status: 200, data: post });
    } else if (dataListRef.current) {
      listHandler.updateList({ ok: true }, JSON.stringify(post), dataListRef.current, setDataList);
    }
    setEditData && setEditData((prev) => (prev?.id === post.id ? { ...prev, ...post } : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customListHander, setDataList, setEditData]);

  // Always keep a ref to the latest formik so intervals/callbacks never go stale.
  const formikRef = useRef(formik);
  useEffect(() => { formikRef.current = formik; });

  // Stable Save component — defining it inside render caused React to unmount/remount
  // the button on every render because it saw a new component type each time.
  const Save = useCallback(() => (
    <SaveButton
      isSaving={isSaving}
      submitFunction={(e) => {
        formikRef.current.handleSubmit(e);
        e.preventDefault();
      }}
    />
  ), [isSaving]);

  // Publishing reads the post from the database, so anything still sitting in the
  // form (title, text) has to be written first — the interval autosave below can be
  // up to 10s behind. Resolves false when the save failed, so the caller can abort.
  const flushSave = useCallback(async () => {
    const fk = formikRef.current;
    if (!fk.dirty || !(fk.values.id > 0)) return true;
    const response = await crudApi.edit(SocialEndpoints.posts.edit, JSON.stringify(fk.values, null, 2), true);
    return response.status === 200;
  }, []);

  useEffect(() => {
    if (!showEdit) return;
    const intervalId = setInterval(() => {
      const fk = formikRef.current;
      if (!fk.dirty || !(fk.values.id > 0)) return;
      // Fire-and-forget: direct API call with no React state updates so autosave
      // never triggers a re-render that freezes the textarea mid-typing.
      crudApi.edit(SocialEndpoints.posts.edit, JSON.stringify(fk.values, null, 2), true);
    }, 10000);
    return () => clearInterval(intervalId);
  }, [showEdit]);

  return (
    <>
      <NewModal
        show={showEdit}
        setShow={setShowEdit}
        title={formik.values.title}
        ExtraButton={Save}
        fullscreen={true}
        onShow={() => {
          editData
            ? formik.setValues(PostForm.setEditData(editData))
            : formik.setValues(Post);
        }}
        onExit={() => {
          if (refreshOnClose) {
            let triggervalue = trigger * -1;
            setTrigger && setTrigger(triggervalue);
          }
          formik.resetForm({ values: { Post } });
        }}
      >
        <Tabs
          defaultActiveKey="compose"
          id="uncontrolled-tab-example"
          className="mb-3"
        >
          <Tab
            eventKey="compose"
            title={<TabTitle icon="uil-pen" label={t("Backend.Post.Tabs.Compose")} />}
          >
            <ComposePostTab
              formik={formik}
              editData={editData}
              setEditData={setEditData}
              pillarsEndpoint={pillarsEndpoint}
              group={group}
            />
          </Tab>
          <Tab
            eventKey="schedule"
            title={<TabTitle icon="uil-calendar-alt" label={t("Backend.Post.Tabs.Schedule")} />}
          >
            <SchedulePostTab post={editData} flushSave={flushSave} onPostChanged={onPostChanged} />
          </Tab>
          <Tab
            eventKey="prompter"
            title={<TabTitle icon="uil-presentation-play" label={t("Backend.Post.Tabs.Prompter")} />}
          >
            <TelepromterWrapper text={editData ? editData.text : ''} />
          </Tab>
          {(editData && editData.stats?.length > 0) && (
            <Tab
              eventKey="stats"
              title={<TabTitle icon="uil-chart-line" label={t("Backend.Post.Tabs.Stats")} />}
            >
              <StatsTab stats={editData.stats} />
            </Tab>
          )}
          <Tab
            eventKey="platformSettings"
            title={<TabTitle icon="uil-sliders-v-alt" label={t("Backend.Post.Tabs.PlatformSettings", "Platform settings")} />}
          >
            <PostPlatformSettings post={editData} show={showEdit} />
          </Tab>
          {share && group && (
            <Tab
              eventKey="groupComments"
              title={<TabTitle icon="uil-comments-alt" label={t("Backend.Groups.Comments.TabTitle")} />}
            >
              <GroupCommentsTab share={share} group={group} />
            </Tab>
          )}
        </Tabs>
      </NewModal>
    </>
  );
};

export default PostFormView;
